import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import isEmpty from 'validator/lib/isEmpty';

import { setBirthdaySet } from '../../../../domains/Auth/slice';
import { Button } from '../../../../GlobalComponents/Button';
import { ErrorMessage } from '../../../../GlobalComponents/ErrorMessage';
import {
	ButtonContainer,
	ComboContainer,
	Form,
	LeftColumn,
	RightColumn
} from '../../../../GlobalComponents/Form';
import { Input } from '../../../../GlobalComponents/Input';
import { Select } from '../../../../GlobalComponents/Select';
import { SuccessMessage } from '../../../../GlobalComponents/SuccessMessage';
import { useAppDispatch, useAppSelector } from '../../../../reduxstore/hooks';
import { states } from '../../../../utils/select/selectOptions';
import { useUpdateProfileInfoMutation } from '../../../api';
import { setProfile } from '../../../state/profileSlice';

const ProfileData = ({ profileData }: ProfileDataProps): JSX.Element => {
	const [updateProfileInfo, { isError, isLoading, isSuccess, error }] =
		useUpdateProfileInfoMutation();

	const [profileState, setProfileState] = useState<IProfileData>(
		profileData as IProfileData
	);
	const userId = useAppSelector(state => state.authReducer.id);
	const disableBirthday = useAppSelector(
		state => state.authReducer.birthdaySet
	);
	const dispatch = useAppDispatch();

	useEffect(() => {
		setProfileState(profileData as IProfileData);
	}, [profileData]);

	const handleChanges = (event: React.ChangeEvent<HTMLInputElement>) => {
		setProfileState({
			...profileState,
			[event.target.name]: event.target.value
		});
	};

	const validateProfile = (): boolean => {
		const errors: IErrors = {};
		let formIsValid = true;

		if (isEmpty(profileState.first_name)) {
			formIsValid = false;
			errors['firstName'] = 'First Name cannot be empty';
		}
		if (isEmpty(profileState.last_name)) {
			formIsValid = false;
			errors['lastName'] = 'Last Name cannot be empty';
		}
		if (
			profileState.first_name.match(
				/[!@#$%^&*()_+=`~'";:/?.>,<\\|{}[\]]/g
			)
		) {
			formIsValid = false;
			errors['firstName'] =
				'Please remove special characters in first name.';
		}

		if (
			profileState.last_name.match(/[!@#$%^&*()_+=`~'";:/?.>,<\\|{}[\]]/g)
		) {
			formIsValid = false;
			errors['lastName'] =
				'Please remove special characters in last name.';
		}

		if (profileState?.street && isEmpty(profileState.street)) {
			formIsValid = false;
			errors['street'] = 'Address cannot be empty';
		}
		if (profileState?.city && isEmpty(profileState.city)) {
			formIsValid = false;
			errors['city'] = 'City cannot be empty';
		}
		if (profileState?.postal_code && isEmpty(profileState.postal_code)) {
			formIsValid = false;
			errors['postal_code'] = 'Zip cannot be empty';
		}
		if (profileState?.birth_date && isEmpty(profileState.birth_date)) {
			formIsValid = false;
			errors['birth_date'] = 'Birthday cannot be empty';
		}
		if (
			profileState?.birth_date &&
			!isEmpty(profileState.birth_date) &&
			!DateTime.fromFormat(profileState?.birth_date, 'yyyy-MM-dd').isValid
		) {
			formIsValid = false;
			errors['birth_date'] = 'Select valid Birthday';
		}
		setProfileState({
			...profileState,
			errors: errors
		});

		return formIsValid;
	};

	const onProfileSubmit = (event: any): void => {
		event?.preventDefault();

		try {
			if (validateProfile()) {
				updateProfileInfo({
					...profileState,
					id: userId,
					birthday_set: disableBirthday
				})
					.unwrap()
					.then((res: ProfileResponse) => {
						window.scrollTo({ top: 0, behavior: 'smooth' });
						dispatch(setProfile(res.data.profile));
						dispatch(setBirthdaySet(res.data.birthday_set));
					});
			}
		} catch (error: any) {
			setProfileState({
				...profileState
			});
		}
	};

	const resolveState = () => {
		const st = profileState.state;
		let value = '';
		states.forEach(state => {
			if (st?.toUpperCase() === state.toUpperCase()) {
				value = state;
			}
		});
		return value;
	};

	return (
		<>
			{isSuccess && (
				<SuccessMessage message="Profile successfully updated!" />
			)}
			{isError && <ErrorMessage error={error}></ErrorMessage>}
			<Form onSubmit={onProfileSubmit}>
				<LeftColumn>
					<ComboContainer>
						<Input
							name="first_name"
							label="First Name"
							type="text"
							value={profileState.first_name}
							onChange={handleChanges}
							size={'half'}
							$error={
								profileState.errors
									? profileState.errors['firstName']
									: null
							}
						/>
						<Input
							label="Last Name"
							name="last_name"
							onChange={handleChanges}
							type="text"
							value={profileState.last_name}
							size={'half'}
							$error={
								profileState.errors &&
								profileState.errors['lastName']
							}
						/>
					</ComboContainer>

					<ComboContainer>
						<Input
							label="Birth Day"
							name="birth_date"
							placeholder=""
							type="date"
							value={profileState.birth_date || ''}
							onChange={handleChanges}
							$error={
								profileState.errors &&
								profileState.errors['birth_date']
							}
							min={DateTime.now()
								.minus({ years: 120 })
								.toFormat('yyyy-MM-dd')}
							max={DateTime.now().toFormat('yyyy-MM-dd')}
							disabled={disableBirthday}
						/>
					</ComboContainer>
					{disableBirthday && (
						<span
							style={{
								fontSize: '12px',
								lineHeight: '110%',
								color: '#757575'
							}}
						>
							** Contact the Fanatic Factory to update your
							birthday.
						</span>
					)}
				</LeftColumn>
				<RightColumn>
					<Input
						label="Address"
						name="street"
						onChange={handleChanges}
						type="text"
						value={profileState.street}
						size={'full'}
						$error={
							profileState.errors && profileState.errors['street']
						}
					/>

					<ComboContainer>
						<Input
							label="City"
							name="city"
							onChange={handleChanges}
							type="text"
							value={profileState.city}
							inputSize={'half'}
							$error={
								profileState.errors &&
								profileState.errors['city']
							}
						/>
						<Select
							label="State"
							name="state"
							onChange={handleChanges}
							placeholder="Select.."
							$inputSize={'half'}
							values={states}
							selected={profileState.state}
							keyValue={resolveState()}
						/>
					</ComboContainer>
					<Input
						label="Postal Code"
						name="postal_code"
						onChange={handleChanges}
						type="number"
						inputmode="number"
						pattern="[0-9]*"
						value={profileState.postal_code}
						size={'full'}
						$error={
							profileState.errors && profileState.errors['zip']
						}
					/>
					<ButtonContainer>
						<Button
							type="submit"
							$isLoading={isLoading}
							disabled={isLoading}
						>
							SAVE
						</Button>
					</ButtonContainer>
				</RightColumn>
			</Form>
		</>
	);
};

export default ProfileData;

interface ProfileDataProps {
	profileData?: ProfileState;
}
interface IProfileData extends ProfileState {
	birthdayMonth?: string;
	birthdayDate?: string;
	errors: IErrors | null;
}
interface IErrors {
	[key: string]: string | null;
}
